export class ApiError extends Error {
  public status: number
  public details?: unknown

  constructor(message: string, status: number, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.details = details
  }
}

export class NotAuthenticatedError extends ApiError {
  constructor(message = 'Authentication required', details?: unknown) {
    super(message, 401, details)
    this.name = 'NotAuthenticatedError'
  }
}

type UnauthorizedHandler = (info?: { message?: string; status?: number; details?: unknown }) => void

let unauthorizedHandler: UnauthorizedHandler | null = null

export const setUnauthorizedHandler = (handler: UnauthorizedHandler | null | undefined) => {
  if (handler === undefined || handler === null) {
    unauthorizedHandler = null
    return
  }

  if (typeof handler !== 'function') {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[API] setUnauthorizedHandler ignored non-function handler', handler)
    }
    unauthorizedHandler = null
    return
  }

  unauthorizedHandler = handler
}

const notifyUnauthorized = (info: { message?: string; status?: number; details?: unknown }) => {
  try {
    unauthorizedHandler?.(info)
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[API] unauthorized handler threw', err)
    }
  }
}

const RESPONSE_TYPES = ['json', 'blob', 'text', 'void'] as const

type ResponseType = (typeof RESPONSE_TYPES)[number]
type RequestBody = BodyInit | Record<string, unknown> | null | undefined

export interface HealthResponse {
  status: string
}

export interface User {
  id: string
  user_id?: number
  email: string
  name?: string
  role?: string
}

export interface AuthResponse {
  access_token: string
  token_type?: string
  refresh_token?: string
  user?: User
}

export interface RegisterRequest {
  email: string
  password: string
  name?: string
}

export interface AuthRegisterRequest {
  username: string
  password: string
}

export interface LoginRequest {
  email: string
  password: string
}

export interface AuthLoginRequest {
  username: string
  password: string
}

export interface FigureContext {
  id: number
  figure_slug: string
  source_name?: string | null
  source_url?: string | null
  content_type?: string | null
  content: string
  is_manual: number
}

export interface HistoricalFigure {
  id?: number
  slug: string
  name: string
  era?: string | null
  roles?: string | string[] | null
  image_url?: string | null
  short_summary?: string | null
  persona_prompt?: string | null
  long_bio?: string | null
  pit_narrative?: string | null
  pit_narrative_updated_at?: string | null
  pit_narrative_format?: 'markdown' | 'plain' | null
  echo_story?: string | null
  quote?: string | null
  birth_year?: number | null
  death_year?: number | null
  floruit_start_year?: number | null
  floruit_end_year?: number | null
  main_site?: string | null
  related_places?: string[] | string | null
  related_sites?: string[] | string | null
  associated_places?: string[] | string | null
  era_label?: string | null
  known_for?: string | null
  pronunciation?: string | null
  mini_timeline?: string | unknown[] | null
  did_you_know?: string | null
  talk_topics?: string | string[] | null
  related_figures?: string | string[] | null
  sources?: Record<string, unknown> | null
  wiki_links?: Record<string, string> | null
  verified?: boolean | number | null
  contexts?: FigureContext[]
  source_depth?: SourceDepthSummary | null
}

export interface FigureLite {
  slug: string
  name: string
  image_url?: string | null
  short_summary?: string | null
  persona_prompt?: string | null
  era_label?: string | null
  pronunciation?: string | null
  sources?: Record<string, unknown> | null
  source_depth?: SourceDepthSummary | null
}

export type SourceDepthLevel = 'foundation' | 'referenced' | 'developed' | 'deep_archive'

export interface SourceDepthSummary {
  level: SourceDepthLevel
  label: string
  context_count: number
  source_count: number
  source_types?: string[]
  corpus_lanes?: string[]
  has_primary_sources?: boolean
  has_external_urls?: boolean
  has_publication_dates?: boolean
  tooltip?: string | null
}

const FORBIDDEN_FIGURE_KEYS = ['contexts', 'figure_contexts'] as const

const findForbiddenFigureKeys = (value: unknown): string[] => {
  if (!value || typeof value !== 'object') return []
  const keys = Object.keys(value as Record<string, unknown>)
  return keys.filter((key) => FORBIDDEN_FIGURE_KEYS.includes(key as (typeof FORBIDDEN_FIGURE_KEYS)[number]))
}

const logFigurePayloadDebug = (label: string, payload: unknown) => {
  if (!import.meta.env.DEV) return
  try {
    const json = JSON.stringify(payload)
    const sizeBytes = json ? json.length : 0
    const forbidden = findForbiddenFigureKeys(payload)
    // eslint-disable-next-line no-console
    console.debug(`[FIGURE_LITE] ${label} payload ~${sizeBytes} bytes`)
    if (forbidden.length) {
      // eslint-disable-next-line no-console
      console.warn(`[FIGURE_LITE] ${label} payload contains forbidden keys: ${forbidden.join(', ')}`)
    }
  } catch {
    // ignore debug logging errors
  }
}

// Full figure context must only be loaded in admin/debug views. Chat surfaces must stick to this trimmed shape.
export const toFigureLite = (figure?: HistoricalFigure | null): FigureLite => {
  const legacy = (figure as Record<string, unknown> | null) ?? null
  const imageCandidate =
    figure?.image_url ??
    // Accept legacy fields but collapse them into image_url so we never leak the full object.
    (legacy?.['image_thumb_url'] as string | undefined) ??
    (legacy?.['thumbnail_url'] as string | undefined) ??
    (legacy?.['portrait_small'] as string | undefined) ??
    (legacy?.['image_alt_url'] as string | undefined) ??
    null

  logFigurePayloadDebug('toFigureLite', figure)

  return {
    slug: figure?.slug ?? 'unknown',
    name: figure?.name ?? figure?.slug ?? 'Unknown figure',
    image_url: typeof imageCandidate === 'string' ? imageCandidate : null,
    short_summary: figure?.short_summary ?? null,
    persona_prompt: figure?.persona_prompt ?? null,
    era_label: figure?.era_label ?? null,
    pronunciation: figure?.pronunciation ?? null,
    sources: figure?.sources ?? null,
    source_depth: figure?.source_depth ?? null,
  }
}

export interface FavoriteFigure {
  id?: string
  figure_slug: string
  figure?: HistoricalFigure
  added_at?: string
}

export type QuothaMode = 'off' | 'auto' | 'on'
export type QuothaStatus = 'none' | 'pending' | 'ready' | 'failed'

export const DEFAULT_QUOTHA_MODE: QuothaMode = 'auto'

const normalizeQuothaModeValue = (value?: QuothaMode | null): QuothaMode => {
  if (value === 'on' || value === 'off') return value
  return DEFAULT_QUOTHA_MODE
}

export interface QuothaPanelEvidenceItem {
  title?: string | null
  url?: string | null
  snippet?: string | null
  note?: string | null
}

export interface QuothaPanelPayload {
  title?: string | null
  verdict?: string | null
  support?: string | null
  micro_commentary?: string | null
  evidence_basis?: string | null
  strongest_support?: string | null
  needs_caution?: string | null
  speculative_or_reconstructed?: string | null
  what_would_improve_confidence?: string | null
  assessment?: string | null
  summary?: string | null
  evidence?: QuothaPanelEvidenceItem[] | null
  gaps?: string[] | null
  notes?: string | null
  body?: string | null
  bullets?: string[] | null
  meta?: Record<string, unknown> | null
  [key: string]: unknown
}

export interface AskRequest {
  prompt: string
  figure_slug?: string
  thread_id?: string
}

export interface AskSource {
  id?: string
  title?: string
  source_label?: string
  source_name?: string
  work_title?: string
  attribution?: string
  source_title?: string
  section?: string
  display_excerpt?: string
  snippet?: string
  passage?: string
  full_passage?: string
  chunk_text?: string
  content?: string
  url?: string
  source_url?: string
  source_type?: string
  corpus_lane?: string
  content_type?: string
  chunk_id?: string
  doc_id?: string
  chunk_index?: number | string | null
  source_pub_date?: string
  publication_date?: string
  active_version?: string
  active_rag_version?: string
}

export interface AskUsage {
  input_tokens?: number
  output_tokens?: number
  cost?: number
}

export interface AnswerMeta {
  sources_available?: boolean
  sources_count?: number
  works_present?: string[]
  knowledge_basis?: string
}

export interface EpistemicPayload {
  retrieval_similarity_band?: string | null
  active_rag_version?: string | null
  uncertainty_flags?: unknown[] | null
}

export type QuothaFlagSeverity = 'info' | 'warning'

export interface QuothaFlag {
  code: string
  message: string
  severity?: QuothaFlagSeverity
}

export interface QuothaBundle {
  flags: QuothaFlag[]
  panel: QuothaPanelPayload | null
  status?: QuothaStatus | null
}

export interface AskResponse {
  answer?: string
  message?: string
  thread_id?: string
  assistant_message_id?: string
  sources?: AskSource[]
  usage?: AskUsage
  quotha_panel?: QuothaPanelPayload | null
  quotha?: QuothaBundle
  quotha_status?: QuothaStatus | null
  quotha_flags?: QuothaFlag[]
  answer_meta?: AnswerMeta
  sources_available?: boolean
  sources_count?: number
  works_present?: string[]
  epistemic?: EpistemicPayload | null
}

export type LanguageModeOption = 'modern' | 'period' | 'period_authentic'
export type KnowledgeScopeOption = 'historical_only' | 'bounded' | 'full_context'
export type ToneLevelOption = 'child' | 'teen' | 'adult'

export interface UserSettingsPayload {
  language_mode?: LanguageModeOption
  knowledge_scope?: KnowledgeScopeOption
  tone_level?: ToneLevelOption
  quotha_annotations?: boolean
}

export interface UserSettingsResponse extends UserSettingsPayload {
  defaults?: UserSettingsPayload
  can_edit_tone?: boolean
  is_guest?: boolean
  updated_at?: string
}

export interface GuestStartResponse {
  guest_token: string
  figure_slug: string
  thread_id: string
}

export interface GuestAskRequest {
  prompt: string
  thread_id: string
  guest_token: string
}

export interface GuestAskResponse {
  answer: string
  thread_id: string
  sources?: AskSource[]
}

export interface Thread {
  id: string
  figure_slug?: string
  title?: string
  created_at?: string
  updated_at?: string
  last_message?: string | null
  language_mode_override?: LanguageModeOption | null
  knowledge_scope_override?: KnowledgeScopeOption | null
  tone_level_override?: ToneLevelOption | null
  quotha_mode?: QuothaMode | null
}

export interface ThreadMessage {
  id?: string
  role?: 'user' | 'assistant' | 'system'
  content?: string
  created_at?: string
  quotha_panel?: QuothaPanelPayload | null
  quotha?: QuothaBundle
  quotha_status?: QuothaStatus | null
  quotha_flags?: QuothaFlag[]
  sources?: AskSource[]
  answer_meta?: AnswerMeta
  sources_available?: boolean
  sources_count?: number
  works_present?: string[]
  knowledge_basis?: string | null
  epistemic?: EpistemicPayload | null
}

export interface ThreadAskRequest {
  message: string
  figure_slug?: string | null
  source_page?: string | null
  model_used?: string | null
  skip_llm?: boolean | null
  age_profile?: string | null
  quotha_mode?: QuothaMode | null
  quotha?: boolean | null
}

export interface ThreadQuothaResponse {
  status?: QuothaStatus | null
  quotha_panel?: QuothaPanelPayload | null
  quotha?: QuothaBundle | null
  quotha_flags?: QuothaFlag[]
  epistemic?: EpistemicPayload | null
}

export interface ContextSnippetRequest {
  figureSlug: string
  chunkId: string
}

export interface ContextSnippetResponse {
  chunk_id?: string | null
  chunk_text?: string | null
  content?: string | null
  snippet?: string | null
  text?: string | null
  doc_id?: string | null
  source_title?: string | null
  publication_date?: string | null
  source_pub_date?: string | null
  active_rag_version?: string | null
}

export interface ThreadCreate {
  figure_slug?: string
  title?: string
}

export interface ThreadSettingsPayload {
  language_mode_override?: LanguageModeOption | null
  knowledge_scope_override?: KnowledgeScopeOption | null
  tone_level_override?: ToneLevelOption | null
  quotha_mode?: QuothaMode | null
}

export interface AdminFigureCreate {
  slug: string
  name: string
  summary?: string
  era_primary?: string
  teaser?: string
  tags?: string[]
}

export interface AdminFigureUpdate extends Partial<AdminFigureCreate> {}

export interface RagSourceCreate {
  name: string
  figure_slug?: string
  type?: string
  url?: string
  content?: string
}

export interface RagSource {
  id: string
  name: string
  figure_slug?: string
  type?: string
  url?: string
  content?: string
  created_at?: string
  updated_at?: string
}

export interface RagContext {
  id: string
  figure_slug: string
  source_id?: string
  content: string
  created_at?: string
  updated_at?: string
}

export interface RagContextUpdate {
  content?: string
  figure_slug?: string
  source_id?: string
}

export interface FigureListParams {
  skip?: number
  limit?: number
}

export interface RagContextListParams {
  figure_slug?: string
}

export interface PlaceSummaries {
  gen?: string
  [key: string]: string | undefined
}

export interface PlaceEcho {
  title?: string
  text?: string
}

export interface PlaceMap {
  google_embed?: string
  googleEmbed?: string
  [key: string]: string | undefined
}

export interface PlaceLinks {
  wikipedia?: string
  official_site?: string
  officialSite?: string
  [key: string]: string | undefined
}

export interface Place {
  id?: string
  slug: string
  name: string
  description?: string
  summary?: string
  summary_gen?: string
  teaser?: string
  siteType?: string
  primaryEra?: string
  era_primary?: string
  era_range?: string
  timeline_start?: number
  timeline_end?: number
  country?: string
  region?: string
  location_label?: string
  locationLabel?: string
  latitude?: number
  longitude?: number
  types?: string[]
  summaries?: PlaceSummaries
  echo?: PlaceEcho
  echo_title?: string
  echo_text?: string
  map?: PlaceMap
  map_google_embed?: string
  links?: PlaceLinks
  link_wikipedia?: string
  link_official_site?: string
  hero_image?: string | null
  heroImage?: string | null
  echo_image?: string | null
  echoImage?: string | null
  [key: string]: unknown
}

export interface PlaceListParams {
  skip?: number
  limit?: number
  search?: string
  country?: string
  era?: string
}

export interface ContentMediaAsset {
  id?: string | number
  url?: string | null
  alt?: string | null
  caption?: string | null
  credit?: string | null
  width?: number | null
  height?: number | null
  kind?: string | null
  focalPointX?: number | null
  focalPointY?: number | null
  [key: string]: unknown
}

export interface ContentLink {
  label?: string | null
  url?: string | null
  target?: string | null
  description?: string | null
  icon?: string | null
  [key: string]: unknown
}

export interface ContentItem {
  id: string | number
  slug: string
  title: string
  subtitle?: string | null
  summary?: string | null
  excerpt?: string | null
  body?: string | null
  category?: string | null
  type?: string | null
  content_type?: string | null
  tags?: string[] | null
  status?: string | null
  featured?: boolean | null
  is_featured?: boolean | null
  featured_rank?: number | null
  hero_image?: string | null
  heroImage?: string | null
  thumbnail_image?: string | null
  thumbnailImage?: string | null
  promo_image?: string | null
  promoImage?: string | null
  published_at?: string | null
  updated_at?: string | null
  expiry_date?: string | null
  expiryDate?: string | null
  read_time_minutes?: number | null
  cta_label?: string | null
  cta_url?: string | null
  external_url?: string | null
  externalUrl?: string | null
  source_name?: string | null
  attachments?: ContentMediaAsset[] | null
  links?: ContentLink[] | null
  related_places?: string[] | null
  related_figures?: string[] | null
  metadata?: Record<string, unknown> | null
  [key: string]: unknown
}

export interface ContentListParams {
  skip?: number
  limit?: number
  search?: string
  contentType?: string
  types?: string[]
  tags?: string[]
  category?: string
  featured?: boolean
  status?: string
  before?: string
  after?: string
  placeSlug?: string
  figureSlug?: string
  isFeatured?: boolean
  sort?: string
  order?: 'asc' | 'desc'
}

const normalizeQuothaBundle = (response?: { quotha?: QuothaBundle | null; quotha_panel?: QuothaPanelPayload | null; quotha_status?: QuothaStatus | null } | null): QuothaBundle => {
  const raw = response?.quotha
  if (raw && typeof raw === 'object') {
    const flags = Array.isArray(raw.flags)
      ? raw.flags.filter((flag): flag is QuothaFlag => Boolean(flag && typeof flag === 'object' && 'code' in flag && 'message' in flag))
      : []
    return {
      flags,
      panel: raw.panel ?? null,
      status: raw.status ?? response?.quotha_status ?? null,
    }
  }

  return {
    flags: [],
    panel: response?.quotha_panel ?? null,
    status: response?.quotha_status ?? null,
  }
}

const normalizeAskResponse = (response?: AskResponse | null): AskResponse => {
  const base = response && typeof response === 'object' ? response : {}
  const sources = Array.isArray(base.sources) ? base.sources : []
  const quotha = normalizeQuothaBundle(base as { quotha?: QuothaBundle | null; quotha_panel?: QuothaPanelPayload | null })

  return {
    ...base,
    sources,
    quotha,
    quotha_panel: quotha.panel ?? null,
    quotha_status: quotha.status ?? base.quotha_status ?? null,
  }
}

export class ApiClient {
  private baseUrl: string
  private userIdCache?: string | number

  constructor(baseUrl: string = 'http://localhost:8000') {
    this.baseUrl = baseUrl.replace(/\/$/, '')
  }

  getAuthHeaders(token?: string): Record<string, string> {
    if (!token) return {}
    return { Authorization: `Bearer ${token}` }
  }

  private ensureToken(token?: string): string {
    if (!token) {
      throw new NotAuthenticatedError()
    }
    return token
  }

  private buildUrl(path: string): string {
    if (path.startsWith('http')) {
      return path
    }
    const cleanPath = path.startsWith('/') ? path : `/${path}`
    return `${this.baseUrl}${cleanPath}`
  }

  private prepareBody(body: RequestBody, headers: Headers): BodyInit | undefined {
    if (body === null || body === undefined) {
      return undefined
    }

    if (body instanceof FormData || typeof body === 'string' || body instanceof Blob || body instanceof ArrayBuffer || body instanceof URLSearchParams) {
      return body
    }

    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }

    return JSON.stringify(body)
  }

  private async request<T>(
    path: string,
    options: RequestInit & { body?: RequestBody } = {},
    token?: string,
    responseType: ResponseType = 'json',
  ): Promise<T> {
    const headers = new Headers({ ...this.getAuthHeaders(token), ...(options.headers ?? {}) })

    const preparedBody = this.prepareBody(options.body, headers)

    const response = await fetch(this.buildUrl(path), {
      ...options,
      headers,
      body: preparedBody,
    })

    if (!response.ok) {
      let details: unknown
      let message = response.statusText || 'Request failed'

      try {
        details = await response.clone().json()
        if (details && typeof details === 'object' && 'detail' in details) {
          const detailValue = (details as { detail?: unknown }).detail
          if (typeof detailValue === 'string') {
            message = detailValue
          }
        }
        if (details && typeof details === 'object' && 'message' in details) {
          const detailValue = (details as { message?: unknown }).message
          if (typeof detailValue === 'string') {
            message = detailValue
          }
        }
      } catch {
        try {
          const text = await response.clone().text()
          if (text) {
            details = text
            message = text
          }
        } catch {
          // ignore
        }
      }

      if (response.status === 401) {
        notifyUnauthorized({ message, status: response.status, details })
      }

      throw new ApiError(message, response.status, details)
    }

    if (responseType === 'void') {
      return undefined as T
    }

    if (responseType === 'blob') {
      const blob = await response.blob()
      return blob as T
    }

    if (responseType === 'text') {
      const text = await response.text()
      return text as T
    }

    if (response.status === 204) {
      return undefined as T
    }

    const text = await response.text()
    if (!text) {
      return undefined as T
    }

    return JSON.parse(text) as T
  }

  health(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/health', { method: 'GET' })
  }

  register(payload: RegisterRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>('/register', { method: 'POST', body: payload })
  }

  login(payload: LoginRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>('/login', { method: 'POST', body: payload })
  }

  authLogin(payload: AuthLoginRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/login', { method: 'POST', body: payload })
  }

  authRegister(payload: AuthRegisterRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/register', { method: 'POST', body: payload })
  }

  async getCurrentUser(token?: string): Promise<User> {
    const authToken = this.ensureToken(token)
    const user = await this.request<User>('/auth/me', { method: 'GET' }, authToken)
    if (user?.user_id || user?.id) {
      this.userIdCache = user.user_id ?? user.id
    }
    return user
  }

  adminStepUp(token: string): Promise<AuthResponse> {
    return this.request<AuthResponse>('/auth/admin/stepup', { method: 'POST' }, token)
  }

  async listFigures(params?: FigureListParams): Promise<HistoricalFigure[]> {
    const searchParams = new URLSearchParams()
    if (typeof params?.skip === 'number') {
      searchParams.set('skip', String(params.skip))
    }
    if (typeof params?.limit === 'number') {
      searchParams.set('limit', String(params.limit))
    }
    const query = searchParams.toString()
    const path = query ? `/figures/?${query}` : '/figures/'
    const data = await this.request<HistoricalFigure[] | { items?: HistoricalFigure[] }>(path, { method: 'GET' })
    if (Array.isArray(data)) {
      return data
    }
    return data.items ?? []
  }

  searchFigures(query: string, limit?: number): Promise<HistoricalFigure[]> {
    const trimmed = query.trim()
    const searchParams = new URLSearchParams({ q: trimmed })
    if (typeof limit === 'number') {
      searchParams.set('limit', String(limit))
    }
    return this.request<HistoricalFigure[]>(`/figures/search?${searchParams.toString()}`, { method: 'GET' })
  }

  getFigure(slug: string): Promise<HistoricalFigure> {
    return this.request<HistoricalFigure>(`/figures/${encodeURIComponent(slug)}`, { method: 'GET' })
  }

  // Backend now serves the lite shape at /figures/:slug; do not call the full figure endpoint from chat surfaces.
  async getFigureLite(slug: string): Promise<FigureLite> {
    const figure = await this.request<FigureLite>(`/figures/${encodeURIComponent(slug)}`, { method: 'GET' })
    logFigurePayloadDebug(`getFigureLite:${slug}`, figure)
    return figure
  }

  async listFavoriteFigures(token: string): Promise<FavoriteFigure[]> {
    const data = await this.request<FavoriteFigure[] | { items?: FavoriteFigure[] }>(
      '/figures/favorites',
      { method: 'GET' },
      token,
    )
    if (Array.isArray(data)) {
      return data
    }
    return data.items ?? []
  }

  addFavoriteFigure(slug: string, token: string): Promise<FavoriteFigure> {
    return this.request<FavoriteFigure>(`/figures/favorites/${encodeURIComponent(slug)}`, { method: 'POST' }, token)
  }

  removeFavoriteFigure(slug: string, token: string): Promise<void> {
    return this.request<void>(`/figures/favorites/${encodeURIComponent(slug)}`, { method: 'DELETE' }, token, 'void')
  }

  async listPlaces(params?: PlaceListParams): Promise<Place[]> {
    const searchParams = new URLSearchParams()
    if (typeof params?.skip === 'number') {
      searchParams.set('skip', String(params.skip))
    }
    if (typeof params?.limit === 'number') {
      searchParams.set('limit', String(params.limit))
    }
    if (params?.search) {
      searchParams.set('search', params.search)
    }
    if (params?.country) {
      searchParams.set('country', params.country)
    }
    if (params?.era) {
      searchParams.set('era', params.era)
    }
    if (params?.contentType) {
      searchParams.set('content_type', params.contentType)
    }
    if (params?.types?.length) {
      params.types.forEach((type) => {
        if (type) {
          searchParams.append('content_type', type)
        }
      })
    }
    const query = searchParams.toString()
    const path = query ? `/places?${query}` : '/places'
    const data = await this.request<Place[] | { items?: Place[] }>(path, { method: 'GET' })
    if (Array.isArray(data)) {
      return data
    }
    return data.items ?? []
  }

  getPlace(slug: string): Promise<Place> {
    return this.request<Place>(`/places/${encodeURIComponent(slug)}`, { method: 'GET' })
  }

  async listThreads(token?: string, userId?: string | number): Promise<Thread[]> {
    const authToken = this.ensureToken(token)
    let targetId: string | number | undefined = userId ?? this.userIdCache

    if (!targetId) {
      const me = await this.getCurrentUser(authToken)
      targetId = me.user_id ?? me.id
      if (targetId) this.userIdCache = targetId
    }

    if (!targetId) {
      throw new ApiError('Unable to determine user id for threads lookup', 400)
    }

    const path = `/threads/user/${encodeURIComponent(String(targetId))}`
    const data = await this.request<Thread[] | { items?: Thread[] }>(path, { method: 'GET' }, authToken)
    if (Array.isArray(data)) {
      return data
    }
    return data.items ?? []
  }

  getThread(threadId: string, token?: string): Promise<Thread> {
    const authToken = this.ensureToken(token)
    return this.request<Thread>(`/threads/${encodeURIComponent(threadId)}`, { method: 'GET' }, authToken)
  }

  updateThreadSettings(threadId: string, payload: ThreadSettingsPayload, token?: string): Promise<Thread> {
    const authToken = this.ensureToken(token)
    const safePayload: ThreadSettingsPayload = { ...payload }

    if (payload.quotha_mode !== undefined) {
      safePayload.quotha_mode = normalizeQuothaModeValue(payload.quotha_mode)
    }

    return this.request<Thread>(`/threads/${encodeURIComponent(threadId)}/settings`, {
      method: 'PATCH',
      body: safePayload,
    }, authToken)
  }

  async listThreadMessages(threadId: string, token?: string): Promise<ThreadMessage[]> {
    const authToken = this.ensureToken(token)
    const data = await this.request<ThreadMessage[] | { items?: ThreadMessage[] }>(
      `/threads/${encodeURIComponent(threadId)}/messages`,
      { method: 'GET' },
      authToken,
    )
    if (Array.isArray(data)) return data
    return data.items ?? []
  }

  getThreadMessageQuotha(threadId: string, messageId: string, token?: string): Promise<ThreadQuothaResponse> {
    const authToken = this.ensureToken(token)
    return this.request<ThreadQuothaResponse>(
      `/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(messageId)}/quotha`,
      { method: 'GET' },
      authToken,
    )
  }

  getContextSnippet(params: ContextSnippetRequest, token?: string): Promise<ContextSnippetResponse> {
    const authToken = this.ensureToken(token)
    return this.request<ContextSnippetResponse>(
      `/figures/${encodeURIComponent(params.figureSlug)}/context/${encodeURIComponent(params.chunkId)}`,
      { method: 'GET' },
      authToken,
    )
  }

  private buildContentSearchParams(params?: ContentListParams): URLSearchParams {
    const searchParams = new URLSearchParams()
    if (typeof params?.skip === 'number') {
      searchParams.set('skip', String(params.skip))
    }
    if (typeof params?.limit === 'number') {
      searchParams.set('limit', String(params.limit))
    }
    if (params?.search) {
      searchParams.set('search', params.search)
    }
    params?.types?.forEach((type) => {
      if (type) {
        searchParams.append('type', type)
      }
    })
    params?.tags?.forEach((tag) => {
      if (tag) {
        searchParams.append('tag', tag)
      }
    })
    if (params?.category) {
      searchParams.set('category', params.category)
    }
    if (typeof params?.featured === 'boolean') {
      searchParams.set('featured', params.featured ? 'true' : 'false')
    }
    if (typeof params?.isFeatured === 'boolean') {
      searchParams.set('is_featured', params.isFeatured ? 'true' : 'false')
    }
    if (params?.status) {
      searchParams.set('status', params.status)
    }
    if (params?.before) {
      searchParams.set('before', params.before)
    }
    if (params?.after) {
      searchParams.set('after', params.after)
    }
    if (params?.placeSlug) {
      searchParams.set('place_slug', params.placeSlug)
    }
    if (params?.figureSlug) {
      searchParams.set('figure_slug', params.figureSlug)
    }
    if (params?.sort) {
      searchParams.set('sort', params.sort)
    }
    if (params?.order) {
      searchParams.set('order', params.order)
    }
    return searchParams
  }

  async listContent(params?: ContentListParams): Promise<ContentItem[]> {
    const searchParams = this.buildContentSearchParams(params)
    const query = searchParams.toString()
    const path = query ? `/content?${query}` : '/content'
    const data = await this.request<ContentItem[] | { items?: ContentItem[] }>(path, { method: 'GET' })
    if (Array.isArray(data)) {
      return data
    }
    return data.items ?? []
  }

  listNews(params?: ContentListParams): Promise<ContentItem[]> {
    const mergedParams: ContentListParams = {
      limit: params?.limit ?? 12,
      status: params?.status ?? 'published',
      contentType: params?.contentType ?? 'news_article',
      ...params,
    }

    if (!mergedParams.contentType && (!mergedParams.types || mergedParams.types.length === 0)) {
      mergedParams.types = ['news_article']
    }

    const searchParams = this.buildContentSearchParams(mergedParams)
    if (!searchParams.has('content_type')) {
      searchParams.set('content_type', 'news_article')
    }

    const query = searchParams.toString()
    const path = query ? `/news?${query}` : '/news'
    return this.request<ContentItem[]>(path, { method: 'GET' })
  }

  getContentItem(slug: string): Promise<ContentItem> {
    return this.request<ContentItem>(`/content/${encodeURIComponent(slug)}`, { method: 'GET' })
  }

  getNewsItem(slug: string): Promise<ContentItem> {
    return this.request<ContentItem>(`/news/${encodeURIComponent(slug)}`, { method: 'GET' })
  }

  /**
   * Legacy chat endpoint: retained for reference. The live /chat route uses src/chat/api/chatApiClient.ts instead.
   */
  ask(payload: AskRequest, token?: string): Promise<AskResponse> {
    return this.request<AskResponse>('/ask', { method: 'POST', body: payload }, token)
      .then((response) => normalizeAskResponse(response))
  }

  async askThread(threadId: string, payload: ThreadAskRequest, token?: string): Promise<AskResponse> {
    const authToken = this.ensureToken(token)

    const safePayload: ThreadAskRequest = {
      message: payload.message,
      quotha_mode: normalizeQuothaModeValue(payload.quotha_mode),
    }

    if (payload.figure_slug !== undefined) safePayload.figure_slug = payload.figure_slug ?? null
    if (payload.source_page !== undefined) safePayload.source_page = payload.source_page ?? null
    if (payload.model_used !== undefined) safePayload.model_used = payload.model_used ?? null
    if (payload.skip_llm !== undefined) safePayload.skip_llm = payload.skip_llm ?? null
    if (payload.age_profile !== undefined) safePayload.age_profile = payload.age_profile ?? null
    if (payload.quotha !== undefined) safePayload.quotha = Boolean(payload.quotha)

    try {
      const response = await this.request<AskResponse>(
        `/threads/${encodeURIComponent(threadId)}/ask`,
        { method: 'POST', body: safePayload },
        authToken,
      )
      return normalizeAskResponse(response)
    } catch (error) {
      if (error instanceof ApiError && error.status === 422 && import.meta?.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn('Thread ask validation error', error.details ?? error.message)
      }
      throw error
    }
  }

  /**
   * Legacy guest chat starter. Real guest chat is handled by src/chat/api/chatApiClient.ts.
   */
  guestStart(figureSlug: string): Promise<GuestStartResponse> {
    const params = new URLSearchParams({ figure_slug: figureSlug })
    return this.request<GuestStartResponse>(`/guest/start?${params.toString()}`, { method: 'POST' })
  }

  /**
   * Legacy guest chat ask helper (unused).
   */
  guestAsk(payload: GuestAskRequest): Promise<GuestAskResponse> {
    return this.request<GuestAskResponse>('/guest/ask', { method: 'POST', body: payload })
  }

  getUserSettings(token?: string): Promise<UserSettingsResponse> {
    const authToken = this.ensureToken(token)
    return this.request<UserSettingsResponse>('/user/settings', { method: 'GET' }, authToken)
  }

  updateUserSettings(payload: UserSettingsPayload, token?: string): Promise<UserSettingsResponse> {
    const authToken = this.ensureToken(token)
    return this.request<UserSettingsResponse>('/user/settings', { method: 'POST', body: payload }, authToken)
  }

  async createThread(payload: ThreadCreate, token?: string): Promise<Thread> {
    const authToken = this.ensureToken(token)

    const response = await fetch(this.buildUrl('/threads'), {
      method: 'POST',
      headers: { ...this.getAuthHeaders(authToken), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload ?? {}),
    })

    if (!response.ok) {
      let details: unknown
      let message = response.statusText || 'Request failed'
      try {
        const parsed = await response.clone().json()
        details = parsed
        if (parsed && typeof parsed === 'object') {
          if ('detail' in parsed && typeof (parsed as { detail?: unknown }).detail === 'string') {
            message = (parsed as { detail?: string }).detail as string
          }
          if ('message' in parsed && typeof (parsed as { message?: unknown }).message === 'string') {
            message = (parsed as { message?: string }).message as string
          }
        }
      } catch {
        try {
          const text = await response.clone().text()
          if (text) {
            details = text
            message = text
          }
        } catch {
          // ignore
        }
      }

      throw new ApiError(message, response.status, details)
    }

    const tryExtractId = (candidate: unknown): string | undefined => {
      if (!candidate || typeof candidate !== 'object') return undefined
      const obj = candidate as Record<string, unknown>
      if (typeof obj.id === 'string' || typeof obj.id === 'number') return String(obj.id)
      if (typeof obj.thread_id === 'string' || typeof obj.thread_id === 'number') return String(obj.thread_id)
      if (obj.thread && typeof obj.thread === 'object') {
        const nested = obj.thread as Record<string, unknown>
        if (typeof nested.id === 'string' || typeof nested.id === 'number') return String(nested.id)
      }
      if (obj.data && typeof obj.data === 'object') {
        const nested = obj.data as Record<string, unknown>
        if (typeof nested.id === 'string' || typeof nested.id === 'number') return String(nested.id)
      }
      return undefined
    }

    let parsedBody: unknown
    let threadId: string | undefined

    try {
      const text = await response.clone().text()
      if (text) {
        parsedBody = JSON.parse(text)
        threadId = tryExtractId(parsedBody)
      }
    } catch {
      // Swallow JSON parse errors; they are not fatal for successful responses.
    }

    if (!threadId) {
      const locationHeader = response.headers.get('Location')
      if (locationHeader) {
        const segments = locationHeader.split('/').filter(Boolean)
        const last = segments[segments.length - 1]
        if (last) {
          threadId = decodeURIComponent(last)
        }
      }
    }

    if (!threadId) {
      const threads = await this.listThreads(token)
      const sorted = [...threads].sort((a, b) => {
        const aTime = new Date(a.updated_at ?? a.created_at ?? 0).getTime()
        const bTime = new Date(b.updated_at ?? b.created_at ?? 0).getTime()
        return bTime - aTime
      })
      threadId = sorted[0]?.id
      if (!parsedBody) {
        parsedBody = sorted[0]
      }
    }

    if (!threadId) {
      throw new ApiError('Unable to derive thread id after creation', response.status)
    }

    return { ...(parsedBody && typeof parsedBody === 'object' ? (parsedBody as Record<string, unknown>) : {}), id: threadId } as Thread
  }

  deleteThread(threadId: string, token: string): Promise<void> {
    const authToken = this.ensureToken(token)
    return this.request<void>(`/threads/${encodeURIComponent(threadId)}`, { method: 'DELETE' }, authToken, 'void')
  }

  renameThread(threadId: string, title: string, token: string): Promise<Thread> {
    const authToken = this.ensureToken(token)
    return this.request<Thread>(
      `/threads/${encodeURIComponent(threadId)}/title`,
      { method: 'PATCH', body: { title } },
      authToken,
    )
  }

  downloadDb(token?: string): Promise<Blob> {
    const authToken = this.ensureToken(token)
    return this.request<Blob>('/download_db', { method: 'GET' }, authToken, 'blob')
  }

  async adminListFigures(token: string): Promise<HistoricalFigure[]> {
    const data = await this.request<HistoricalFigure[] | { items?: HistoricalFigure[] }>(
      '/admin/figures',
      { method: 'GET' },
      token,
    )
    if (Array.isArray(data)) {
      return data
    }
    return data.items ?? []
  }

  adminCreateFigure(payload: AdminFigureCreate, token: string): Promise<HistoricalFigure> {
    return this.request<HistoricalFigure>('/admin/figures', { method: 'POST', body: payload }, token)
  }

  adminUpdateFigure(slug: string, payload: AdminFigureUpdate, token: string): Promise<HistoricalFigure> {
    return this.request<HistoricalFigure>(`/admin/figures/${encodeURIComponent(slug)}`, { method: 'PATCH', body: payload }, token)
  }

  adminDeleteFigure(slug: string, token: string): Promise<void> {
    return this.request<void>(`/admin/figures/${encodeURIComponent(slug)}`, { method: 'DELETE' }, token, 'void')
  }

  adminCreateRagSource(payload: RagSourceCreate, token: string): Promise<RagSource> {
    return this.request<RagSource>('/admin/rag/sources', { method: 'POST', body: payload }, token)
  }

  adminListRagContexts(params: RagContextListParams | undefined, token: string): Promise<RagContext[]> {
    const searchParams = new URLSearchParams()
    if (params?.figure_slug) {
      searchParams.set('figure_slug', params.figure_slug)
    }
    const query = searchParams.toString()
    const path = query ? `/admin/rag/contexts?${query}` : '/admin/rag/contexts'
    return this.request<RagContext[]>(path, { method: 'GET' }, token)
  }

  adminUpdateRagContext(ctxId: string, payload: RagContextUpdate, token: string): Promise<RagContext> {
    return this.request<RagContext>(`/admin/rag/contexts/${encodeURIComponent(ctxId)}`, { method: 'PATCH', body: payload }, token)
  }

  adminDeleteRagContext(ctxId: string, token: string): Promise<void> {
    return this.request<void>(`/admin/rag/contexts/${encodeURIComponent(ctxId)}`, { method: 'DELETE' }, token, 'void')
  }
 }
